/**
 * ConfirmModal - Reusable confirmation dialog
 * 
 * Replaces window.confirm() with a styled modal that matches FSP design.
 * Uses Radix AlertDialog under the hood.
 */

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";

export default function ConfirmModal({
  open,
  onOpenChange,
  title = "Confirm Action",
  message = "Are you sure you want to proceed?",
  confirmText = "Confirm",
  cancelText = "Cancel",
  onConfirm,
  onCancel,
  variant = "default", // "default" | "destructive"
}) {
  const handleConfirm = () => {
    if (onConfirm) onConfirm();
    if (onOpenChange) onOpenChange(false);
  };

  const handleCancel = () => {
    if (onCancel) onCancel();
    if (onOpenChange) onOpenChange(false);
  };

  const confirmButtonClass = variant === "destructive"
    ? "bg-red-600 hover:bg-red-700 text-white"
    : "bg-[#FF6600] hover:bg-[#CC5200] text-white";

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="confirm-modal">
        <AlertDialogHeader>
          <AlertDialogTitle className="font-heading text-lg">
            {title}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-zinc-600">
            {message}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            onClick={handleCancel}
            className="border-2"
            data-testid="confirm-modal-cancel"
          >
            {cancelText}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            className={confirmButtonClass}
            data-testid="confirm-modal-confirm"
          >
            {confirmText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

/**
 * useConfirmModal - Hook for easy confirm modal usage
 * 
 * Usage:
 *   const { confirm, ConfirmDialog } = useConfirmModal();
 *   
 *   const handleDelete = async () => {
 *     const confirmed = await confirm({
 *       title: "Delete Item",
 *       message: "Are you sure?",
 *       variant: "destructive",
 *     });
 *     if (confirmed) { ... }
 *   };
 *   
 *   return (
 *     <>
 *       <button onClick={handleDelete}>Delete</button>
 *       <ConfirmDialog />
 *     </>
 *   );
 */
import { useState, useCallback } from "react";

export function useConfirmModal() {
  const [state, setState] = useState({
    open: false,
    title: "Confirm",
    message: "Are you sure?",
    confirmText: "Confirm",
    cancelText: "Cancel",
    variant: "default",
    resolve: null,
  });

  const confirm = useCallback((options = {}) => {
    return new Promise((resolve) => {
      setState({
        open: true,
        title: options.title || "Confirm",
        message: options.message || "Are you sure?",
        confirmText: options.confirmText || "Confirm",
        cancelText: options.cancelText || "Cancel",
        variant: options.variant || "default",
        resolve,
      });
    });
  }, []);

  const handleConfirm = useCallback(() => {
    if (state.resolve) state.resolve(true);
    setState((s) => ({ ...s, open: false, resolve: null }));
  }, [state.resolve]);

  const handleCancel = useCallback(() => {
    if (state.resolve) state.resolve(false);
    setState((s) => ({ ...s, open: false, resolve: null }));
  }, [state.resolve]);

  const handleOpenChange = useCallback((open) => {
    if (!open && state.resolve) {
      state.resolve(false);
    }
    setState((s) => ({ ...s, open, resolve: open ? s.resolve : null }));
  }, [state.resolve]);

  const ConfirmDialog = useCallback(() => (
    <ConfirmModal
      open={state.open}
      onOpenChange={handleOpenChange}
      title={state.title}
      message={state.message}
      confirmText={state.confirmText}
      cancelText={state.cancelText}
      variant={state.variant}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
    />
  ), [state, handleOpenChange, handleConfirm, handleCancel]);

  return { confirm, ConfirmDialog };
}
